/* eslint-disable no-unused-vars */
/* eslint-disable max-lines */
import React, {
    useEffect, useState, useContext
} from 'react';
import {
    useParams
} from 'react-router-dom';
import {
    Globe, Send
} from 'lucide-react';
import {
    apiManagerService
} from 'shared/api/services/BEX/apiManager.service';
import {
    ApiForm
} from 'builders/builderInternal/components/ApiManager/ApiForm';
import {
    Context
} from 'shared/context/GlobalState';
import DSResourceList from './DSResourceList';

/**
 * DSApisList — DS-themed list for Library → APIs.
 * Create/Edit open the M51 ApiForm in a Dialog inline (the form already
 * inherits the DS palette via the HSL token cascade). No more redirect to
 * the legacy `/ai-lab/api-manager` hub.
 */
export default function DSApisList() {
    const {
        oragID, subOragID
    } = useParams();
    const ctx = useContext(Context) || {};
    const [items, setItems] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [formOpen, setFormOpen] = useState(false);
    const [formInitial, setFormInitial] = useState(null);
    const [formMode, setFormMode] = useState('new');

    function reload() {
        if (!oragID || !subOragID) return;
        setLoading(true);
        setError(null);
        apiManagerService.listApis(oragID, subOragID)
            .then((res) => {
                const arr = res?.data || res || [];
                setItems(Array.isArray(arr) ? arr : []);
            })
            .catch((err) => setError(err?.message || 'Failed to load'))
            .finally(() => setLoading(false));
    }
    useEffect(reload, [oragID, subOragID]);

    function openNew() {
        setFormInitial({
            name: '',
            url: '',
            method: 'GET',
            timeout: 30000,
            headers: {},
            paramsDef: [],
            auth: {
                type: 'none'
            },
            input_schema: '',
            output_schema: ''
        });
        setFormMode('new');
        setFormOpen(true);
    }
    function openEdit(api) {
        setFormInitial({
            ...api,
            input_schema: typeof api.input_schema === 'string'
                ? api.input_schema : (api.input_schema ? JSON.stringify(api.input_schema, null, 2) : ''),
            output_schema: typeof api.output_schema === 'string'
                ? api.output_schema : (api.output_schema ? JSON.stringify(api.output_schema, null, 2) : '')
        });
        setFormMode('edit');
        setFormOpen(true);
    }
    async function handleSave(formData) {
        try {
            const apiObj = {
                organizationId: oragID,
                subOrganizationId: subOragID,
                name: formData.name,
                url: formData.url,
                timeout: formData.timeout,
                method: formData.method,
                headers: formData.headers,
                paramsDef: formData.paramsDef,
                auth: formData.auth || { type: 'none' },
            };
            // Always send schemas (ApiForm normalises empty -> {}) so clearing a schema
            // actually overwrites the stored one. {} means "don't validate".
            apiObj.input_schema = (formData.input_schema && typeof formData.input_schema === 'object')
                ? formData.input_schema : {};
            apiObj.output_schema = (formData.output_schema && typeof formData.output_schema === 'object')
                ? formData.output_schema : {};

            if (formMode === 'edit' && formData._id) {
                apiObj.apiId = formData._id;
            }
            await apiManagerService.persistApiWithVaultAuth(
                apiObj,
                formData._vaultSecrets || {},
                formData._headerVaultSecrets || {},
                formData._keepSecretHeaderNames || [],
            );
            setFormOpen(false);
            reload();
        } catch (err) {
            alert('Save failed: ' + (err?.response?.data?.message || err?.message || String(err)));
        }
    }
    async function handleDelete(api) {
        if (!window.confirm(`Delete API "${api.name || api._id}"?`)) return;
        try {
            await apiManagerService.deleteApi({
                organizationId: oragID,
                subOrganizationId: subOragID,
                apiId: api._id
            });
            reload();
        } catch (err) {
            alert('Delete failed: ' + (err?.response?.data?.message || err?.message || String(err)));
        }
    }

    return (
        <>
            <DSResourceList
                items={items}
                loading={loading}
                error={error}
                getKey={(it) => it._id}
                getCard={(it) => ({
                    icon: Globe,
                    tone: 'violet',
                    title: it.name || it._id,
                    subtitle: it.url,
                    description: it.description || '',
                    version: it.version ? `v${it.version}` : null,
                    badge: it.method ? { tone: 'neutral', label: it.method } : undefined,
                    stats: [
                        it.auth?.type && it.auth.type !== 'none' && {
                            icon: Send,
                            label: `auth ${it.auth.type}`
                        }
                    ].filter(Boolean),
                    idLine: it._id,
                    createdBy: it.createdBy || null,
                    createdAt: it.created || it.createdAt || null,
                    searchTerms: [it.method, it.url, it.name]
                })}
                onPick={openEdit}
                onEdit={openEdit}
                onDelete={handleDelete}
                onCreate={openNew}
                newLabel="New API"
                searchPlaceholder="Search APIs by name, method, URL…"
                emptyTitle="No APIs yet"
                emptyDescription="Add your first REST endpoint to plug your assistants into the outside world."
                emptyIcon={Globe}
            />
            {formOpen && (
                <ApiForm
                    api={formInitial}
                    onSave={handleSave}
                    onCancel={() => setFormOpen(false)}
                    open={formOpen}
                    setOpen={setFormOpen}
                />
            )}
        </>
    );
}
